import React from 'react';
import { Typography, Input, Space, Button, Spin, Breadcrumb } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useLibrary } from '../../context/library';
import { useBanners } from '../../context/banners';
import { useModal } from '../../context/modal';
import { useBannerFilters } from '../../hooks/banners/library/use-banner-filters';
import { BannerCard } from './banner-viewer-card';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { getFolderName } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const { Text } = Typography;

const DISPLAY_COUNT = 30;

const generateBreadcrumbItems = (
  currentFolder: string | undefined,
  onNavigate: (path: string) => void
): BreadcrumbItemType[] => {
  const items: BreadcrumbItemType[] = [
    {
      title: 'Todos',
      onClick: () => onNavigate('/library'),
      className: 'breadcrumb-item'
    }
  ];

  if (!currentFolder) {
    return items;
  }

  const pathParts = currentFolder.split('/');

  // Para cada parte, crear un item del breadcrumb
  pathParts.forEach((_, index) => {
    // Construir la ruta completa hasta este punto
    // Ejemplo: index 0 -> "Espn", index 1 -> "Espn/Futbol"
    const pathUpToHere = pathParts.slice(0, index + 1).join('/');

    const formattedName = getFolderName(pathUpToHere);

    items.push({
      title: formattedName,
      onClick: () => onNavigate(`/library/${encodeURIComponent(pathUpToHere)}`),
      className: 'breadcrumb-item'
    });
  });

  return items;
};

const BannerGrid: React.FC = () => {
  const { fontSize, screens, spacing } = useResponsiveSizes();
  const { banners, currentFolder, isLoading } = useLibrary();
  const { refreshAllBanners, isRefreshingAll } = useBanners();
  const { searchTerm, setSearchTerm, totalItems, filteredBanners } = useBannerFilters(banners);
  const { selectedAssetId } = useModal();
  const navigate = useNavigate();

  const [displayCount, setDisplayCount] = React.useState(50);

  //  Generar los items del breadcrumb
  const breadcrumbItems = React.useMemo(
    () => generateBreadcrumbItems(currentFolder, navigate),
    [currentFolder, navigate]
  );

  const isModalOpen = !!selectedAssetId;
  const stableBannersRef = React.useRef(banners);

  if (!isModalOpen) {
    stableBannersRef.current = banners;
  }

  const displayBanners = isModalOpen ? stableBannersRef.current : banners;

  // Banners visibles (limitados por displayCount)
  const visibleBanners = filteredBanners.slice(0, displayCount);
  const hasMore = displayCount < filteredBanners.length;

  // Reset displayCount cuando cambia la carpeta o búsqueda
  React.useEffect(() => {
    setDisplayCount(DISPLAY_COUNT);
  }, [currentFolder, searchTerm]);

  // Configuración responsive de columnas
  const columnsCountBreakPoints = {
    350: 1, // móvil
    640: 2, // tablet pequeña
    1024: 3, // tablet/laptop pequeño
    1440: 4, // desktop
    1920: 5 // pantallas grandes
  };

  if (isLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          margin: screens.xs ? '100px 0' : '150px 0',
          color: '#999',
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin indicator={<LoadingOutlined spin />} size='large' />
      </div>
    );
  }

  if (displayBanners.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          margin: '50px',
          color: '#999',
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Text style={{ fontSize: fontSize.base }}>No hay assets en esta carpeta</Text>
      </div>
    );
  }

  return (
    <div>
      {/* Controles superiores */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Input.Search
            placeholder='Buscar...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: 200 }}
            styles={{ input: { fontSize: fontSize.sm } }}
            allowClear
          />

          <Button
            icon={<ReloadOutlined />}
            onClick={refreshAllBanners}
            loading={isRefreshingAll}
            title='Actualizar banners'
            style={{ backgroundColor: 'white', color: '#242424', position: 'relative' }}
          />
        </Space>

        <Text style={{ fontSize: fontSize.sm }}>
          {visibleBanners.length} de {totalItems} assets
        </Text>
      </Space>

      {/* Grid */}
      <>
        <div style={{ display: 'flex' }}>
          <Breadcrumb
            items={breadcrumbItems}
            style={{
              fontSize: fontSize.base,
              fontWeight: 500,
              marginBottom: '8px'
            }}
            separator={<ChevronRight width={screens.xs ? 10 : 15} />}
          />
        </div>

        <motion.div
          key={currentFolder || 'all'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          style={{ paddingTop: '16px' }}
        >
          <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints}>
            <Masonry gutter={screens.xs ? '12px' : '16px'}>
              {visibleBanners.map((banner, index) => (
                <motion.div
                  key={banner.bannerId}
                  style={{ width: '100%' }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.03,
                    ease: [0.25, 0.1, 0.25, 1]
                  }}
                >
                  <BannerCard banner={banner} />
                </motion.div>
              ))}
            </Masonry>
          </ResponsiveMasonry>

          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '20px' }}>
              <div
                style={{
                  display: 'inline-block',
                  padding: '2px',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Button
                  size='large'
                  onClick={() => setDisplayCount(prev => prev + 50)}
                  style={{
                    height: screens.xs ? 40 : 48,
                    padding: `0 ${spacing.xl}px`,
                    fontSize: fontSize.base,
                    borderRadius: '22px',
                    border: 'none',
                    background: '#ffffff',
                    color: '#262626',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cargar más · {filteredBanners.length - displayCount} restantes
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </>
    </div>
  );
};

export default BannerGrid;
